import type { LanguageCode } from "./i18n";

type AuthCopy = {
  login: {
    eyebrow: string;
    title: string;
    intro: string;
    loginTab: string;
    registerTab: string;
    email: string;
    password: string;
    passwordHint: string;
    accountType: string;
    customer: string;
    contractor: string;
    realEstateAgent: string;
    firstName: string;
    lastName: string;
    company: string;
    phone: string;
    optional: string;
    terms: string;
    submitLogin: string;
    submitRegister: string;
    pending: string;
    secure: string;
    newHere: string;
    alreadyRegistered: string;
    confirmationEyebrow: string;
    confirmationTitle: string;
    confirmationText: string;
    confirmationHint: string;
    resend: string;
    resent: string;
    callbackTitle: string;
    callbackText: string;
    errors: Record<
      | "configuration"
      | "invalid_credentials"
      | "email_not_confirmed"
      | "rate_limit"
      | "expired_link"
      | "unknown",
      string
    >;
  };
  account: {
    eyebrow: string;
    title: string;
    intro: string;
    agentTitle: string;
    agentIntro: string;
    signedInAs: string;
    profile: string;
    name: string;
    company: string;
    email: string;
    phone: string;
    accountType: string;
    accountTypeLabels: Record<"kunde" | "auftragnehmer" | "immobilienmakler" | "admin", string>;
    notProvided: string;
    verification: string;
    emailVerified: string;
    providerStatus: string;
    membership: string;
    contactAccess: string;
    accessGranted: string;
    accessLocked: string;
    browseJobs: string;
    browseProperties: string;
    constructionAccessNotApplicable: string;
    logout: string;
    loggingOut: string;
    loading: string;
    status: Record<"pending" | "approved" | "rejected" | "suspended", string>;
    statusText: Record<"pending" | "approved" | "rejected" | "suspended", string>;
    agentStatusText: Record<"pending" | "approved" | "rejected" | "suspended", string>;
    membershipStatus: Record<"free" | "active" | "inactive", string>;
    helpTitle: string;
    helpText: string;
    completeProfile: string;
    completeProfileText: string;
  };
};

export const authCopy = {
  de: {
    login: {
      eyebrow: "Mein Begner-Konto",
      title: "Ihr sicherer Zugang zu Begner",
      intro:
        "Melden Sie sich an oder erstellen Sie ein Kunden-, Auftragnehmer- oder Immobilienmaklerkonto.",
      loginTab: "Anmelden",
      registerTab: "Konto erstellen",
      email: "E-Mail-Adresse",
      password: "Passwort",
      passwordHint: "Mindestens 8 Zeichen",
      accountType: "Art des Kontos",
      customer: "Privatperson",
      contractor: "Bau-Auftragnehmer",
      realEstateAgent: "Immobilienmakler",
      firstName: "Vorname",
      lastName: "Nachname",
      company: "Unternehmen",
      phone: "Telefonnummer",
      optional: "optional",
      terms: "Ich akzeptiere die AGB und die Datenschutzerklärung von Begner.",
      submitLogin: "Sicher anmelden",
      submitRegister: "Konto erstellen",
      pending: "Bitte einen Moment …",
      secure: "Ihre Sitzung wird sicher in HttpOnly-Cookies gespeichert.",
      newHere: "Noch kein Begner-Konto?",
      alreadyRegistered: "Bereits registriert?",
      confirmationEyebrow: "Fast geschafft",
      confirmationTitle: "Bitte bestätigen Sie Ihre E-Mail-Adresse.",
      confirmationText:
        "Wir haben Ihnen einen Bestätigungslink gesendet. Danach können Sie sich anmelden und Ihr Begner-Konto verwenden.",
      confirmationHint: "Keine Nachricht erhalten? Prüfen Sie auch den Spam-Ordner.",
      resend: "Bestätigung erneut senden",
      resent: "Die Bestätigungs-E-Mail wurde erneut versendet.",
      callbackTitle: "E-Mail wird bestätigt",
      callbackText: "Wir richten Ihren sicheren Zugang gerade fertig ein.",
      errors: {
        configuration:
          "Der Login ist noch nicht vollständig konfiguriert. Bitte kontaktieren Sie uns.",
        invalid_credentials: "E-Mail-Adresse oder Passwort ist nicht korrekt.",
        email_not_confirmed: "Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse.",
        rate_limit: "Zu viele Versuche. Bitte warten Sie kurz und versuchen Sie es erneut.",
        expired_link: "Der Bestätigungslink ist abgelaufen oder wurde bereits verwendet.",
        unknown: "Das hat leider nicht funktioniert. Bitte versuchen Sie es erneut.",
      },
    },
    account: {
      eyebrow: "Mein Begner-Konto",
      title: "Willkommen in Ihrem Auftragnehmerbereich",
      intro:
        "Hier sehen Sie Ihren Prüfstatus, Ihren Pro-Zugang und die Entscheidungen zu Ihren Interessen.",
      agentTitle: "Willkommen in Ihrem Immobilienmaklerbereich",
      agentIntro:
        "Hier sehen Sie den Prüfstatus Ihres Maklerkontos. Bauauftragskontakte bleiben ausschliesslich Bau-Auftragnehmern vorbehalten.",
      signedInAs: "Angemeldet als",
      profile: "Kontodaten",
      name: "Kontaktperson",
      company: "Unternehmen",
      email: "E-Mail-Adresse",
      phone: "Telefonnummer",
      accountType: "Anbieterart",
      accountTypeLabels: {
        kunde: "Kundin oder Kunde",
        auftragnehmer: "Bau-Auftragnehmer",
        immobilienmakler: "Immobilienmakler",
        admin: "Administration",
      },
      notProvided: "Nicht angegeben",
      verification: "Freigabe",
      emailVerified: "E-Mail bestätigt",
      providerStatus: "Prüfstatus",
      membership: "Mitgliedschaft",
      contactAccess: "Interesse bekunden",
      accessGranted: "Mit Pro möglich",
      accessLocked: "Pro-Zugang erforderlich",
      browseJobs: "Aktuelle Aufträge ansehen",
      browseProperties: "Aktuelle Immobilien ansehen",
      constructionAccessNotApplicable: "Nur für Bau-Auftragnehmer",
      logout: "Abmelden",
      loggingOut: "Wird abgemeldet …",
      loading: "Konto wird geladen …",
      status: {
        pending: "In Prüfung",
        approved: "Geprüft",
        rejected: "Nicht freigegeben",
        suspended: "Pausiert",
      },
      statusText: {
        pending:
          "Wir prüfen Ihre Angaben persönlich. Mit geprüftem Konto und Pro-Zugang können Sie Interesse an Aufträgen bekunden.",
        approved:
          "Ihr Unternehmen wurde geprüft. Mit Pro können Sie Interesse bekunden. Kontaktdaten erhalten Sie ausschliesslich nach Auswahl durch den Auftraggeber.",
        rejected:
          "Ihr Konto konnte derzeit nicht freigegeben werden. Unser Team hilft Ihnen bei Rückfragen gerne weiter.",
        suspended:
          "Interessebekundungen und Kontaktzugriff sind für dieses Konto pausiert. Bitte wenden Sie sich an unser Team.",
      },
      agentStatusText: {
        pending:
          "Wir prüfen Ihre Maklerangaben persönlich. Ihr Profil erscheint erst nach der Freigabe im Immobilienbereich.",
        approved:
          "Ihr Immobilienmaklerkonto wurde geprüft. Ein öffentliches Maklerprofil erscheint nach der separaten Freigabe Ihrer vollständigen Firmenangaben.",
        rejected:
          "Ihr Immobilienmaklerkonto konnte derzeit nicht freigegeben werden. Unser Team hilft Ihnen bei Rückfragen gerne weiter.",
        suspended:
          "Ihr Immobilienmaklerprofil ist vorübergehend pausiert. Bitte wenden Sie sich an unser Team.",
      },
      membershipStatus: { free: "Kostenlos", active: "Aktiv", inactive: "Inaktiv" },
      helpTitle: "Fragen zur Prüfung?",
      helpText: "Unser Team unterstützt Sie bei fehlenden Angaben oder Nachweisen.",
      completeProfile: "Firmenprofil zur Prüfung einreichen",
      completeProfileText:
        "Damit wir Ihr Konto freigeben können, reichen Sie bitte Ihr vollständiges Firmenprofil mit Leistungen und Region ein.",
    },
  },
  fr: {
    login: {
      eyebrow: "Mon compte Begner",
      title: "Votre accès sécurisé à Begner",
      intro: "Connectez-vous ou créez un compte client, prestataire ou agent immobilier.",
      loginTab: "Se connecter",
      registerTab: "Créer un compte",
      email: "Adresse e-mail",
      password: "Mot de passe",
      passwordHint: "Au moins 8 caractères",
      accountType: "Type de compte",
      customer: "Cliente ou client",
      contractor: "Prestataire de construction",
      realEstateAgent: "Agent immobilier",
      firstName: "Prénom",
      lastName: "Nom",
      company: "Entreprise",
      phone: "Téléphone",
      optional: "facultatif",
      terms: "J’accepte les CG et la déclaration de protection des données de Begner.",
      submitLogin: "Connexion sécurisée",
      submitRegister: "Créer le compte",
      pending: "Un instant …",
      secure: "Votre session est conservée dans des cookies HttpOnly sécurisés.",
      newHere: "Pas encore de compte Begner ?",
      alreadyRegistered: "Déjà inscrit ?",
      confirmationEyebrow: "Presque terminé",
      confirmationTitle: "Veuillez confirmer votre adresse e-mail.",
      confirmationText:
        "Nous vous avons envoyé un lien de confirmation. Vous pourrez ensuite vous connecter et utiliser votre compte Begner.",
      confirmationHint: "Aucun message reçu ? Vérifiez également vos courriers indésirables.",
      resend: "Renvoyer la confirmation",
      resent: "L’e-mail de confirmation a été renvoyé.",
      callbackTitle: "Confirmation de l’e-mail",
      callbackText: "Nous finalisons votre accès sécurisé.",
      errors: {
        configuration: "La connexion n’est pas encore entièrement configurée. Contactez-nous.",
        invalid_credentials: "L’adresse e-mail ou le mot de passe est incorrect.",
        email_not_confirmed: "Veuillez d’abord confirmer votre adresse e-mail.",
        rate_limit: "Trop de tentatives. Patientez un instant avant de réessayer.",
        expired_link: "Ce lien a expiré ou a déjà été utilisé.",
        unknown: "Une erreur est survenue. Veuillez réessayer.",
      },
    },
    account: {
      eyebrow: "Mon compte Begner",
      title: "Bienvenue dans votre espace prestataire",
      intro:
        "Consultez votre vérification, votre accès Pro et les décisions concernant vos intérêts.",
      agentTitle: "Bienvenue dans votre espace immobilier",
      agentIntro:
        "Consultez ici le statut de votre compte immobilier. Les contacts des chantiers sont réservés aux prestataires de construction.",
      signedInAs: "Connecté en tant que",
      profile: "Données du compte",
      name: "Personne de contact",
      company: "Entreprise",
      email: "Adresse e-mail",
      phone: "Téléphone",
      accountType: "Type de prestataire",
      accountTypeLabels: {
        kunde: "Cliente ou client",
        auftragnehmer: "Prestataire de construction",
        immobilienmakler: "Agent immobilier",
        admin: "Administration",
      },
      notProvided: "Non renseigné",
      verification: "Validation",
      emailVerified: "E-mail confirmé",
      providerStatus: "Statut de vérification",
      membership: "Abonnement",
      contactAccess: "Manifester un intérêt",
      accessGranted: "Disponible avec Pro",
      accessLocked: "Accès Pro requis",
      browseJobs: "Voir les mandats actuels",
      browseProperties: "Voir les biens actuels",
      constructionAccessNotApplicable: "Réservé aux prestataires de construction",
      logout: "Se déconnecter",
      loggingOut: "Déconnexion …",
      loading: "Chargement du compte …",
      status: {
        pending: "En cours de vérification",
        approved: "Vérifié",
        rejected: "Non validé",
        suspended: "Suspendu",
      },
      statusText: {
        pending:
          "Nous vérifions vos informations. Un compte validé et un accès Pro permettent de manifester un intérêt pour les mandats.",
        approved:
          "Votre entreprise a été vérifiée. Avec Pro, vous pouvez manifester un intérêt. Les coordonnées sont disponibles uniquement après sélection par le client.",
        rejected:
          "Votre compte ne peut pas être validé actuellement. Notre équipe répond volontiers à vos questions.",
        suspended:
          "Les intérêts et l’accès aux contacts sont suspendus pour ce compte. Veuillez contacter notre équipe.",
      },
      agentStatusText: {
        pending:
          "Nous vérifions vos informations immobilières. Votre profil apparaîtra uniquement après validation.",
        approved:
          "Votre compte immobilier a été vérifié. Le profil public apparaît après la validation séparée des informations complètes de l’agence.",
        rejected:
          "Votre compte immobilier ne peut pas être validé actuellement. Notre équipe répond volontiers à vos questions.",
        suspended:
          "Votre profil immobilier est temporairement suspendu. Veuillez contacter notre équipe.",
      },
      membershipStatus: { free: "Gratuit", active: "Actif", inactive: "Inactif" },
      helpTitle: "Une question sur la vérification ?",
      helpText: "Notre équipe vous aide à compléter les informations ou justificatifs manquants.",
      completeProfile: "Soumettre le profil de l'entreprise",
      completeProfileText:
        "Pour valider votre compte, envoyez votre profil complet avec vos prestations et votre région.",
    },
  },
  it: {
    login: {
      eyebrow: "Il mio account Begner",
      title: "Il tuo accesso sicuro a Begner",
      intro: "Accedi o crea un account cliente, impresa o agente immobiliare.",
      loginTab: "Accedi",
      registerTab: "Crea account",
      email: "Indirizzo e-mail",
      password: "Password",
      passwordHint: "Almeno 8 caratteri",
      accountType: "Tipo di account",
      customer: "Cliente",
      contractor: "Impresa di costruzione",
      realEstateAgent: "Agente immobiliare",
      firstName: "Nome",
      lastName: "Cognome",
      company: "Azienda",
      phone: "Telefono",
      optional: "facoltativo",
      terms: "Accetto le CG e l’informativa sulla privacy di Begner.",
      submitLogin: "Accedi in modo sicuro",
      submitRegister: "Crea account",
      pending: "Un momento …",
      secure: "La sessione è salvata in cookie HttpOnly sicuri.",
      newHere: "Non hai ancora un account Begner?",
      alreadyRegistered: "Sei già registrato?",
      confirmationEyebrow: "Ci siamo quasi",
      confirmationTitle: "Conferma il tuo indirizzo e-mail.",
      confirmationText:
        "Ti abbiamo inviato un link di conferma. Potrai quindi accedere e utilizzare il tuo account Begner.",
      confirmationHint: "Non hai ricevuto nulla? Controlla anche la cartella spam.",
      resend: "Invia di nuovo la conferma",
      resent: "L’e-mail di conferma è stata inviata di nuovo.",
      callbackTitle: "Conferma dell’e-mail",
      callbackText: "Stiamo completando il tuo accesso sicuro.",
      errors: {
        configuration: "L’accesso non è ancora configurato completamente. Contattaci.",
        invalid_credentials: "L’indirizzo e-mail o la password non è corretto.",
        email_not_confirmed: "Prima conferma il tuo indirizzo e-mail.",
        rate_limit: "Troppi tentativi. Attendi un momento e riprova.",
        expired_link: "Il link è scaduto o è già stato utilizzato.",
        unknown: "Qualcosa non ha funzionato. Riprova.",
      },
    },
    account: {
      eyebrow: "Il mio account Begner",
      title: "Benvenuto nella tua area imprese",
      intro: "Qui trovi lo stato della verifica, l’accesso Pro e le decisioni sui tuoi interessi.",
      agentTitle: "Benvenuto nella tua area immobiliare",
      agentIntro:
        "Qui trovi lo stato del tuo account immobiliare. I contatti dei lavori edili sono riservati alle imprese di costruzione.",
      signedInAs: "Accesso effettuato come",
      profile: "Dati dell’account",
      name: "Persona di contatto",
      company: "Azienda",
      email: "Indirizzo e-mail",
      phone: "Telefono",
      accountType: "Tipo di fornitore",
      accountTypeLabels: {
        kunde: "Cliente",
        auftragnehmer: "Impresa di costruzione",
        immobilienmakler: "Agente immobiliare",
        admin: "Amministrazione",
      },
      notProvided: "Non indicato",
      verification: "Approvazione",
      emailVerified: "E-mail confermata",
      providerStatus: "Stato verifica",
      membership: "Iscrizione",
      contactAccess: "Manifestare interesse",
      accessGranted: "Disponibile con Pro",
      accessLocked: "Accesso Pro richiesto",
      browseJobs: "Vedi gli incarichi attuali",
      browseProperties: "Vedi gli immobili attuali",
      constructionAccessNotApplicable: "Riservato alle imprese di costruzione",
      logout: "Esci",
      loggingOut: "Disconnessione …",
      loading: "Caricamento account …",
      status: {
        pending: "In verifica",
        approved: "Verificato",
        rejected: "Non approvato",
        suspended: "Sospeso",
      },
      statusText: {
        pending:
          "Verifichiamo personalmente i tuoi dati. Un account approvato con accesso Pro permette di manifestare interesse per gli incarichi.",
        approved:
          "La tua impresa è stata verificata. Con Pro puoi manifestare interesse. Ricevi i contatti esclusivamente dopo la selezione da parte del cliente.",
        rejected:
          "Al momento il tuo account non può essere approvato. Il nostro team è disponibile per chiarimenti.",
        suspended:
          "Interessi e accesso ai contatti sono sospesi per questo account. Contatta il nostro team.",
      },
      agentStatusText: {
        pending:
          "Verifichiamo personalmente i tuoi dati immobiliari. Il profilo apparirà solo dopo l’approvazione.",
        approved:
          "Il suo account immobiliare è stato verificato. Il profilo pubblico appare dopo l’approvazione separata dei dati completi dell’agenzia.",
        rejected:
          "Al momento il tuo account immobiliare non può essere approvato. Il nostro team è disponibile per chiarimenti.",
        suspended: "Il tuo profilo immobiliare è temporaneamente sospeso. Contatta il nostro team.",
      },
      membershipStatus: { free: "Gratuita", active: "Attiva", inactive: "Inattiva" },
      helpTitle: "Domande sulla verifica?",
      helpText: "Il nostro team ti aiuta con dati o documenti mancanti.",
      completeProfile: "Invia il profilo aziendale",
      completeProfileText:
        "Per approvare il suo account, invii il profilo completo con servizi e regione.",
    },
  },
  en: {
    login: {
      eyebrow: "My Begner account",
      title: "Your secure access to Begner",
      intro: "Log in or create a customer, contractor, or real-estate agent account.",
      loginTab: "Log in",
      registerTab: "Create account",
      email: "Email address",
      password: "Password",
      passwordHint: "At least 8 characters",
      accountType: "Account type",
      customer: "Customer",
      contractor: "Construction contractor",
      realEstateAgent: "Real-estate agent",
      firstName: "First name",
      lastName: "Last name",
      company: "Company",
      phone: "Phone number",
      optional: "optional",
      terms: "I accept Begner’s terms and privacy policy.",
      submitLogin: "Log in securely",
      submitRegister: "Create account",
      pending: "One moment …",
      secure: "Your session is stored securely in HttpOnly cookies.",
      newHere: "No Begner account yet?",
      alreadyRegistered: "Already registered?",
      confirmationEyebrow: "Almost there",
      confirmationTitle: "Please confirm your email address.",
      confirmationText:
        "We sent you a confirmation link. You can then sign in and use your Begner account.",
      confirmationHint: "No message received? Please also check your spam folder.",
      resend: "Resend confirmation",
      resent: "The confirmation email has been sent again.",
      callbackTitle: "Confirming email",
      callbackText: "We are finishing setting up your secure access.",
      errors: {
        configuration: "Login is not fully configured yet. Please contact us.",
        invalid_credentials: "The email address or password is incorrect.",
        email_not_confirmed: "Please confirm your email address first.",
        rate_limit: "Too many attempts. Please wait a moment and try again.",
        expired_link: "The confirmation link has expired or has already been used.",
        unknown: "Something went wrong. Please try again.",
      },
    },
    account: {
      eyebrow: "My Begner account",
      title: "Welcome to your contractor area",
      intro:
        "View your verification status, Pro access and the decisions on your expressions of interest.",
      agentTitle: "Welcome to your real-estate agent area",
      agentIntro:
        "View the status of your agent account. Construction-job contacts are reserved for construction contractors.",
      signedInAs: "Signed in as",
      profile: "Account details",
      name: "Contact person",
      company: "Company",
      email: "Email address",
      phone: "Phone number",
      accountType: "Provider type",
      accountTypeLabels: {
        kunde: "Customer",
        auftragnehmer: "Construction contractor",
        immobilienmakler: "Real-estate agent",
        admin: "Administration",
      },
      notProvided: "Not provided",
      verification: "Approval",
      emailVerified: "Email confirmed",
      providerStatus: "Verification status",
      membership: "Membership",
      contactAccess: "Express interest",
      accessGranted: "Available with Pro",
      accessLocked: "Pro access required",
      browseJobs: "View current jobs",
      browseProperties: "View current properties",
      constructionAccessNotApplicable: "Construction contractors only",
      logout: "Log out",
      loggingOut: "Logging out …",
      loading: "Loading account …",
      status: {
        pending: "Under review",
        approved: "Verified",
        rejected: "Not approved",
        suspended: "Paused",
      },
      statusText: {
        pending:
          "We personally review your details. An approved account with Pro access lets you express interest in jobs.",
        approved:
          "Your company has been verified. Pro lets you express interest. Contact details are available only after the customer selects your company.",
        rejected:
          "Your account could not be approved at this time. Our team will gladly help with any questions.",
        suspended:
          "Interest submissions and contact access are paused for this account. Please contact our team.",
      },
      agentStatusText: {
        pending:
          "We personally review your agent details. Your profile will appear in the real-estate area after approval.",
        approved:
          "Your real-estate agent account has been verified. A public profile appears after the separate approval of your complete company details.",
        rejected:
          "Your real-estate agent account could not be approved at this time. Our team will gladly help with any questions.",
        suspended: "Your real-estate agent profile is temporarily paused. Please contact our team.",
      },
      membershipStatus: { free: "Free", active: "Active", inactive: "Inactive" },
      helpTitle: "Questions about verification?",
      helpText: "Our team can help with missing details or documentation.",
      completeProfile: "Submit company profile for review",
      completeProfileText:
        "To approve your account, please submit your complete company profile with services and region.",
    },
  },
} satisfies Record<LanguageCode, AuthCopy>;
